import { TRACKER_TYPES, type TrackerPrototype } from '@racemap/sdk/schema/trackers';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { OneMinuteInMillis, OneSecondInMillis } from '@racemap/utilities/consts/time';
import { isInsidersDeviceType } from '@racemap/utilities/functions/insidersDevices';
import { isRRDeviceType } from '@racemap/utilities/functions/raceResult';
import { Alert, Select } from 'antd';
import { type FC, useMemo } from 'react';
import { FormLabel } from 'react-bootstrap';
import useSWR from 'swr';
import { useCurrentUser } from '../../../lib/customHooks';
import { useStore } from '../../../store/reducers';
import { VStack } from '../../BasicComponents/MetaComponent';

interface Props {
  value: TrackerPrototype;
  onChange: (value: TrackerPrototype) => void;
}

const apiClient = RacemapAPIClient.fromWindowLocation();
const fetcher = async (trackerType: TRACKER_TYPES): Promise<Array<TrackerPrototype>> => {
  if (!isRRDeviceType(trackerType) && !isInsidersDeviceType(trackerType)) return [];
  return await apiClient.getAddableTrackers({ trackerType });
};
const { Option } = Select;

export const AddableDeviceChooser: FC<Props> = ({ value, onChange }) => {
  const user = useCurrentUser();
  const trackers = useStore((state) => state.trackers.items);
  const {
    data: devices,
    isLoading,
    error,
  } = useSWR(
    [user?.integrations?.raceResultCustomerId, value.trackerType],
    ([, deviceType]: [string | null, TRACKER_TYPES]) => fetcher(deviceType),
    {
      keepPreviousData: true,
      refreshInterval: OneMinuteInMillis,
      dedupingInterval: 30 * OneSecondInMillis,
      errorRetryInterval: 5000,
    },
  );
  const existingTrackerIds = useMemo(
    () => new Set(Array.from(trackers.values()).map((tracker) => tracker.trackerId)),
    [trackers],
  );
  const validDevices = devices?.filter((device) => !existingTrackerIds.has(device.trackerId));

  const handleDeviceChange = (deviceId: string) => {
    const device = devices?.find((device) => device.trackerId === deviceId);
    if (device == null) return;
    onChange({ ...value, trackerId: device.trackerId, trackerName: device.trackerName });
  };

  if (error) {
    return <Alert message="Failed to load devices" type="error" />;
  }

  return (
    <VStack justify="flex-start">
      <FormLabel>{getLabel(value.trackerType)}</FormLabel>
      <Select
        value={value.trackerId || undefined}
        style={{ minWidth: 200 }}
        onChange={handleDeviceChange}
        loading={isLoading}
        placeholder="Select a device"
        notFoundContent="No devices found"
      >
        {validDevices?.map((validDevice) => (
          <Option key={validDevice.trackerId} value={validDevice.trackerId}>
            {validDevice.trackerName}
          </Option>
        ))}
      </Select>
    </VStack>
  );
};

const getLabel = (trackerType: TRACKER_TYPES): string => {
  switch (trackerType) {
    case TRACKER_TYPES.RRDecoder:
      return 'Race|Result Decoder';
    case TRACKER_TYPES.RRTrackbox:
      return 'Race|Result TrackBox';
    case TRACKER_TYPES.RRUbidium:
      return 'Race|Result Ubudium';
    case TRACKER_TYPES.InsidersInspirit:
      return 'Insiders Inspirit';
    case TRACKER_TYPES.InsidersIntense:
      return 'Insiders Intense';
    default:
      return 'Unknown';
  }
};
